import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingBag, Mail, Lock, User, Chrome, Store } from 'lucide-react';
import { toast } from 'sonner';

export const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('seller');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle, setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await register(name, email, password, role);
      // Backend auto-loga o usuário (cookie de sessão). Mantém user no contexto.
      if (response.user) {
        setUser(response.user);
      }
      toast.success(response.message || 'Cadastro realizado com sucesso!');

      // Fluxo obrigatório: vendedor SEM plano → tela de planos.
      // Cliente → home.
      if (role === 'seller') {
        navigate('/seller/planos', { replace: true, state: { user: response.user } });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Needi
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2" data-testid="register-title">
            Criar conta
          </h1>
          <p className="text-gray-600">Junte-se ao Needi</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Tipo de conta - Destaque para Loja */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de conta
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole('seller')}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  role === 'seller'
                    ? 'border-blue-600 bg-blue-50 shadow-md shadow-blue-100'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="register-role-seller"
              >
                {role === 'seller' && (
                  <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    Popular
                  </span>
                )}
                <Store className={`w-7 h-7 mx-auto mb-2 ${role === 'seller' ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className={`block text-sm font-semibold ${role === 'seller' ? 'text-blue-700' : 'text-gray-900'}`}>Loja</span>
                <span className="block text-[11px] text-gray-500 mt-1">Quero vender</span>
              </button>
              
              <button
                type="button"
                onClick={() => setRole('customer')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  role === 'customer'
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="register-role-customer"
              >
                <User className={`w-7 h-7 mx-auto mb-2 ${role === 'customer' ? 'text-indigo-600' : 'text-gray-400'}`} />
                <span className={`block text-sm font-semibold ${role === 'customer' ? 'text-indigo-700' : 'text-gray-900'}`}>Cliente</span>
                <span className="block text-[11px] text-gray-500 mt-1">Quero comprar</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Seu nome"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="seu@email.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Mín. 6 caracteres"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                role === 'seller'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white'
              }`}
              data-testid="register-submit-btn"
            >
              {loading ? 'Criando conta...' : role === 'seller' ? 'Criar minha loja' : 'Criar conta'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Ou cadastre-se com</span>
              </div>
            </div>

            <button
              onClick={loginWithGoogle}
              className="mt-4 w-full flex items-center justify-center space-x-2 border border-gray-300 bg-white px-6 py-3 rounded-lg hover:bg-gray-50 transition-all"
              data-testid="register-google-btn"
            >
              <Chrome className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Google</span>
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-gray-600">
            Já tem uma conta?{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-700" data-testid="register-login-link">
              Entre aqui
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
